import { call, put, takeLatest } from 'redux-saga/effects';
import api, { withAuthConfig } from 'utils/api';
import { urls } from 'utils/apiUrls';
import { layoutActions as actions } from '.';
import { toast } from 'react-toast';
import { clearSession } from 'utils/auth';

function* doGetUser(data) {
  try {
    const res = yield call(
      api.get,
      urls.getUser + data.payload.userId,
      withAuthConfig,
    );

    yield put(actions.getUserSuccess(res.user));
  } catch (error) {
    yield put(actions.getUserError(error));
  }
}

function* doGetCompanies(data) {
  try {
    const res = yield call(
      api.get,
      urls.getAccounts + data.payload.userId,
      withAuthConfig,
    );

    if (data.payload.successCb) data.payload.successCb(res); //hardcoded id

    yield put(actions.getCompaniesSuccess(res));
  } catch (error) {
    toast.error('Something went wrong while fetching companies');
    yield put(actions.getCompaniesError(error));
  }
}

function* doUpdateSelectedCompany(data) {
  try {
    const { cId, uId } = data.payload;
    const res = yield call(api.get, urls.viewCompany(cId, uId), withAuthConfig);

    yield put(actions.updateSelectedCompanySuccess(res));
  } catch (error) {
    toast.error('Something went wrong while fetching company');
    yield put(actions.updateSelectedCompanyError(error));
  }
}

function* doLogout() {
  try {
    const res = yield call(api.get, urls.logout, withAuthConfig);

    yield put(actions.logOutSuccess(res));
  } catch (error) {
    yield put(actions.logOutError(error));
  }
  clearSession();
  window.location.href = '/';
}

function* doUploadDoc(data) {
  try {
    const res = yield call(api.post, urls.importDocument, data.payload.form, {
      ...withAuthConfig,
      headers: {
        ...withAuthConfig.headers,
        contentType: 'multipart/form-data',
      },
      onUploadProgress: progressEvent => {
        const { loaded, total } = progressEvent;
        let percent = Math.floor((loaded * 100) / total);
        data.payload.setProgress(percent);
      },
    });

    yield put(actions.uploadDocSuccess(res));
    data.payload.successCb(res.document.agrUID);
    // toast.success('Document uploaded successfully');
  } catch (error) {
    yield put(actions.uploadDocError(error));
  }
}

function* doUploadTemplate(data) {
  try {
    const res = yield call(api.post, urls.uploadTemplate, data.payload.form, {
      ...withAuthConfig,
      headers: {
        ...withAuthConfig.headers,
        contentType: 'multipart/form-data',
      },
      onUploadProgress: progressEvent => {
        const { loaded, total } = progressEvent;
        let percent = Math.floor((loaded * 100) / total);
        data.payload.setProgress(percent);
      },
    });

    yield put(actions.uploadTemplateSuccess(res.template));
    if (data.payload.successCb) {
      data.payload.successCb(res.template._id);
    }
    // Removed toast - handled in UI component
  } catch (error) {
    if (data.payload.errorCb) {
      data.payload.errorCb(error?.message || 'Something went wrong');
    }
    yield put(
      actions.uploadTemplateError(error?.message || 'Something went wrong'),
    );
  }
}

function* doCreateFolder(data) {
  const folder = data.payload.folder;
  try {
    const res = yield call(api.post, urls.createFolder, folder, withAuthConfig);

    yield put(actions.createFolderSuccess(res));
    if (data.payload.successCb) data.payload.successCb(res.folder);
    toast.success('Folder created successfully');
  } catch (error) {
    toast.error(
      error?.error?.message || 'Something went wrong while creating folder',
    );
    yield put(actions.createFolderError(error?.error));
  }
}

function* doGetFolders(data) {
  try {
    const res = yield call(
      api.get,
      urls.getFolders(data.payload.companyId),
      withAuthConfig,
    );

    yield put(actions.getFoldersSuccess(res));
  } catch (error) {
    toast.error('Failed to load folders');
    yield put(actions.getFoldersError(error?.error || error));
  }
}

function* doAcceptCompanyInvitation(data) {
  try {
    const res = yield call(
      api.post,
      urls.acceptCompanyInvitation,
      data.payload,
      withAuthConfig,
    );

    if (res.statusCode === 200) {
      yield put(
        actions.acceptCompanyInviteSuccess({
          res,
          taskId: data.payload.taskId,
        }),
      );
      if (data.payload.successCb) data.payload.successCb();
      toast.success('Invitation accepted successfully');
    } else {
      yield put(
        actions.acceptCompanyInviteSuccess({
          res,
          taskId: data.payload.taskId,
        }),
      );
      if (data.payload.errorCb)
        data.payload.errorCb({ message: 'Task already finished' });
      toast.warn('Task already finished');
    }
  } catch (error) {
    if (data.payload.errorCb) data.payload.errorCb(error?.error || error);
    toast.error(
      error?.error?.message ||
        'Something went wrong while accepting invitation',
    );
    yield put(actions.acceptCompanyInviteError(error?.error));
  }
}

function* doDeleteFolder(data) {
  try {
    const { folderId, companyId } = data.payload;
    const deleteUrl = `${urls.deleteFolder}?companyId=${companyId}`;
    const res = yield call(api.delete, deleteUrl, {
      ...withAuthConfig,
      data: {
        folderId,
        action: 'move_to_folder',
        targetFolderId: 'uncategorized',
      },
    });

    yield put(actions.deleteFolderSuccess({ folderId: data.payload.folderId }));
    if (data.payload.successCb) data.payload.successCb();
    toast.success('Folder deleted successfully');
  } catch (error) {
    if (data.payload.errorCb) data.payload.errorCb();
    toast.error(
      error?.error?.message || 'Something went wrong while deleting folder',
    );
    yield put(actions.deleteFolderError(error?.error));
  }
}

function* doUpdateNotificationSettings(data) {
  try {
    const res = yield call(
      api.post,
      urls.updateNotificationSettings,
      data.payload,
      withAuthConfig,
    );

    toast.success('Notification settings saved successfully');
  } catch (error) {
    toast.error(
      error?.error?.message || 'Failed to save notification settings',
    );
  }
}

function* doRemoveNotification(data) {
  try {
    yield call(
      api.delete,
      `user/notifications/${data.payload.taskId}`,
      withAuthConfig,
    );
  } catch (error) {
    // Silently handle notification removal errors
  }
}

function* doMarkChangelogAsRead(data) {
  try {
    const res = yield call(
      api.post,
      urls.markChangelogAsRead,
      { version: data.payload.version },
      withAuthConfig,
    );

    yield put(
      actions.markChangelogAsReadSuccess({ version: data.payload.version }),
    );
  } catch (error) {
    yield put(actions.markChangelogAsReadError(error));
  }
}

export function* layoutSaga() {
  yield takeLatest(actions.getUser.type, doGetUser);
  yield takeLatest(actions.getCompanies.type, doGetCompanies);
  yield takeLatest(actions.updateSelectedCompany.type, doUpdateSelectedCompany);
  yield takeLatest(actions.logOut.type, doLogout);
  yield takeLatest(actions.uploadDoc.type, doUploadDoc);
  yield takeLatest(actions.uploadTemplate.type, doUploadTemplate);
  yield takeLatest(actions.createFolder.type, doCreateFolder);
  yield takeLatest(actions.getFolders.type, doGetFolders);
  yield takeLatest(actions.acceptCompanyInvite.type, doAcceptCompanyInvitation);
  yield takeLatest(actions.deleteFolder.type, doDeleteFolder);
  yield takeLatest(
    actions.updateUserNotificationSettings.type,
    doUpdateNotificationSettings,
  );
  yield takeLatest(actions.removeTaskFromUser.type, doRemoveNotification);
  yield takeLatest(actions.markChangelogAsRead.type, doMarkChangelogAsRead);
}
